import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SortOrder } from 'antd/es/table/interface';
import { RequestData } from '@ant-design/pro-table';
import { ProColumns } from '@ant-design/pro-table/es/typing';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import Table from '../../Common/Table';
import {
  createDefaultHiddenColumns,
  getLatestBusinessPhoneNumber,
  getSorterParams,
  queryFilterParams,
} from '../../../utils';
import { useMessageError } from '../../../hooks/common';
import { useAuth, UserStatusType } from '../../../store/auth';
import { dateFormat, dateTimeFormat } from '../../../contstant';
import { DeleteButton, SyncBusinessButton } from './actions';
import {
  useBusinessPartnersTableGet,
  BusinessPartnerTableRow,
  BusinessStatusDisplay,
} from '../../../hooks/api/businessPartners';
import { TariffNameEnum } from '../../../enums/contracts';
import { ContainerNewEmail } from '../Users/View/BusinessPartners';
import TariffsSelect from '../../Common/TariffsSelect';
import { UserRole } from '../../../enums/user';

export enum BusinessStatusColor {
  'active' = 'green',
  // 'pending' = 'blue',
  'pending' = 'processing',
  'deactivated' = 'red',
  'archived' = 'default',
}

export function getBusinessStatusColor(status: UserStatusType): string {
  // @ts-ignore take a color from the list
  return BusinessStatusColor[status?.toLowerCase()] || 'rgba(0,0,0,.15)';
}

const columns: ProColumns<BusinessPartnerTableRow>[] = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: true,
    renderText: (_, row) => row.fullName,
    hideInSearch: true,
  },
  {
    title: 'Email',
    dataIndex: 'email',
    key: 'email',
    hideInSearch: true,
  },
  {
    title: 'Business Partner#',
    dataIndex: 'businessPartnerNumber',
    key: 'businessPartnerNumber',
    // hideInSearch: true,
  },
  {
    title: 'Phone',
    dataIndex: 'phoneNumber',
    key: 'phoneNumber',
    hideInSearch: true,
    render: (_, record) => (record.phoneNumbers || record.phoneNumber
      ? formatPhoneNumberIntl(getLatestBusinessPhoneNumber(record.phoneNumbers, record.phoneNumber || '-')) || '-'
      : '-'),
  },
  {
    title: 'Contracts',
    dataIndex: 'contracts',
    key: 'contracts',
    render: (_, record) => (
      <ul style={{ paddingInlineStart: '16px' }}>
        {(record.contracts || []).map((item) => (
          <li key={item.contractNumber}>
            {item.contractNumber}
            {item.tariffId ? (<span title={`TariffId ${item.tariffName || ''}`}>{`(${item.tariffId})`}</span>) : ''}
            {/* TODO remove? */}
          </li>
        ))}
      </ul>
    ),
    hideInSearch: true,
  },
  {
    title: 'Tariff Name',
    dataIndex: 'tariffNames',
    key: 'tariffNames',
    valueEnum: TariffNameEnum,
    // valueType: 'select',
    hideInTable: true,
    // renderFormItem: (_, { defaultRender, ...config }) => (
    //   <Select
    //     {...config}
    //     allowClear
    //     placeholder="Select"
    //     mode="multiple"
    //     value={Array.isArray(config.value) ? config.value : config.value?.split(',')}
    //   />
    // ),
    renderFormItem: (_, { defaultRender, ...config }) => (
      <TariffsSelect {...config} />
    ),
  },
  {
    title: 'TariffId',
    dataIndex: 'tariffId',
    key: 'tariffId',
    hideInTable: true,
  },
  {
    title: 'Address',
    dataIndex: 'address',
    key: 'address',
    hideInSearch: true,
    render: (_, {
      city, housenumber, street, postalCode, housenumberAddition,
    }) => (
      [city, street, housenumber, housenumberAddition, postalCode].filter(Boolean).join(', ')
    ),
  },
  {
    title: 'Date of birth',
    dataIndex: 'dateOfBirth',
    key: 'dateOfBirthdateOfBirth',
    hideInSearch: true,
    // valueType: 'dateRange',
    sorter: false,
    // .utc(true)
    renderText: (text) => (dayjs(text).isValid() ? dayjs(text).format(dateFormat) : '-'),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    valueEnum: BusinessStatusDisplay,
    render: (_, record) => (
      <ContainerNewEmail newEmail={record.newEmail} />
    ),
    hideInSearch: true,
    // render: (_, record) => (
    //   <Tag color={getBusinessStatusColor(record.status)} className="capitalize-first-letter">{record.status}</Tag>
    // ),
  },
  {
    title: 'Created at',
    dataIndex: 'createdAt',
    key: 'createdAt',
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
  {
    title: 'Updated at',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
];

function ToolBar() {
  const navigate = useNavigate();

  return [
    /* <Button
      key="add"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        navigate('create');
      }}
    >
      Add
    </Button>, */
  ];
}

export default function BusinessPartners(): React.ReactNode | null {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.role && [UserRole.ADMIN, UserRole.ROOT].includes(user.role);
  const businessPartnersGet = useBusinessPartnersTableGet();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(Date.now());

  /** Re-render table once user?.role is resolved (e.g. after /users/me finishes), so actions column is correct. */
  useEffect(() => {
    setTableKey(Date.now());
  }, [user?.role]);

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<BusinessPartnerTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : '1',
      pageSize: pageSize ? `${pageSize}` : '10',
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return businessPartnersGet.fetch({
      ...newParams,
      tariffNames: newParams?.tariffNames?.split(',') || undefined,
    }).then((response) => {
      if (response) {
        const { data, total } = response;

        return ({
          data: (data || []).map(((item) => ({
            ...item,
            key: item.id,
          }))),
          success: true,
          total,
        });
      }

      return ({ data: [], success: false, total: 0 });
    });
  };

  useMessageError([businessPartnersGet]);

  const defaultHiddenColumns = useMemo(() => createDefaultHiddenColumns(
    ['phone', 'createdAt', 'updatedAt'],
  ), []);

  return (
    <Table<BusinessPartnerTableRow>
      key={tableKey}
      resetKey={() => setTableKey(Date.now())}
      className={clsx('transparent')}
      headerTitle="Business Partners list"
      toolBarRender={isAdmin ? ToolBar : undefined}
      request={tableRequest}
      columns={columns}
      actions={user?.role && [UserRole.MITARBEITER, UserRole.ADMIN, UserRole.ROOT].includes(user?.role) ? [
        ['edit', ({ record }: { record: BusinessPartnerTableRow }) => ({
          title: isAdmin ? 'Edit business partner' : 'View business partner',
          onClick: (e: MouseEvent) => {
            e.preventDefault();
            navigate(`${record.id}/edit`);
          },
        })],
        [SyncBusinessButton, { setTableKey }],
        [DeleteButton, {
          setTableKey,
        }],
      ].filter((action) => isAdmin || action[0] === 'edit') as any : undefined}
      search={{
        searchText: 'Filter',
        resetText: 'Clear',
        style: {
          // display: 'none',
        },
      }}
      options={{
        search: {
          name: 'search',
        },
      }}
      columnsState={{
        persistenceKey: 'pro-table-business-partners',
        persistenceType: 'localStorage',
        defaultValue: defaultHiddenColumns,
      }}
    />
  );
}
